import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiRefreshCw, FiLogOut, FiSearch, FiChevronLeft, FiChevronRight, FiUserCheck, FiZap } from 'react-icons/fi';
import ClawRequestsTab from './ClawRequestsTab';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import logo from '../../assets/images/M_Multicolor.svg';
import '../../styles/AdminDashboard.css';
const { FiEye, FiEyeOff } = require('react-icons/fi') as {
    FiEye: React.FC<{
        size?: number;
    }>;
    FiEyeOff: React.FC<{
        size?: number;
    }>;
};
const PAGE_SIZE = 10;
const Pagination: React.FC<{
    page: number;
    total: number;
    onPage: (p: number) => void;
}> = ({ page, total, onPage }) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1)
        return null;
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);
    const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce<(number | string)[]>((acc, p, i, arr) => {
        if (i > 0 && (p as number) - (arr[i - 1] as number) > 1)
            acc.push('…');
        acc.push(p);
        return acc;
    }, []);
    return (<div className="admin-pagination">
      <span className="admin-pagination__info">Showing {start}–{end} of {total}</span>
      <div className="admin-pagination__controls">
        <button className="admin-pagination__btn" onClick={() => onPage(page - 1)} disabled={page === 1}>
          <FiChevronLeft size={13}/>
        </button>
        {pageNums.map((p, i) => typeof p === 'string'
            ? <span key={`e${i}`} className="admin-pagination__ellipsis">{p}</span>
            : <button key={p} className={`admin-pagination__btn${page === p ? ' admin-pagination__btn--active' : ''}`} onClick={() => onPage(p as number)}>{p}</button>)}
        <button className="admin-pagination__btn" onClick={() => onPage(page + 1)} disabled={page === totalPages}>
          <FiChevronRight size={13}/>
        </button>
      </div>
    </div>);
};
interface Agency {
    companyId: number;
    name: string;
    agencyId: string;
    agentId: string | null;
    isSubscribed: boolean;
}
const AdminDashboardPage: React.FC = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const adminName = user?.name
        || [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ')
        || localStorage.getItem('userName')
        || 'Admin';
    const adminInitial = adminName.charAt(0).toUpperCase();
    const [view, setView] = useState<'agencies' | 'requested' | 'claw'>('agencies');
    const [clawCount, setClawCount] = useState<number | null>(null);
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [agencyPage, setAgencyPage] = useState(1);
    const [requestedUsers, setRequestedUsers] = useState<Agency[]>([]);
    const [requestedLoading, setRequestedLoading] = useState(false);
    const [requestedError, setRequestedError] = useState('');
    const [requestedPage, setRequestedPage] = useState(1);
    const [requestedSearch, setRequestedSearch] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const agencyFetchAbort = useRef<AbortController | null>(null);
    const requestedFetchAbort = useRef<AbortController | null>(null);
    const dedupeById = (list: Agency[]) => {
        const seen = new Set<string>();
        return list.filter(item => seen.has(item.agencyId) ? false : (seen.add(item.agencyId), true));
    };
    const fetchRequestedUsers = useCallback(async () => {
        requestedFetchAbort.current?.abort();
        const controller = new AbortController();
        requestedFetchAbort.current = controller;
        setRequestedLoading(true);
        setRequestedError('');
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/admin/requested/subscriptions`, {
                signal: controller.signal,
                headers: {
                    'Nc-Token': localStorage.getItem('amsToken') || '',
                    'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
            });
            if (!res.ok)
                throw new Error(`Failed to load: ${res.status}`);
            const data = await res.json();
            const raw: Agency[] = Array.isArray(data) ? data : (data.data ?? []);
            setRequestedUsers(dedupeById(raw));
            setRequestedPage(1);
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError')
                return;
            setRequestedError(err instanceof Error ? err.message : 'Failed to load requested users');
        }
        finally {
            setRequestedLoading(false);
        }
    }, []);
    const fetchAgencies = useCallback(async (searchTerm = '') => {
        agencyFetchAbort.current?.abort();
        const controller = new AbortController();
        agencyFetchAbort.current = controller;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/admin/agency/subscriptions?search=${encodeURIComponent(searchTerm)}`, {
                signal: controller.signal,
                headers: {
                    'Nc-Token': localStorage.getItem('amsToken') || '',
                    'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
            });
            if (!res.ok)
                throw new Error(`Failed to load: ${res.status}`);
            const data = await res.json();
            const raw: Agency[] = Array.isArray(data) ? data : (data.data ?? []);
            setAgencies(dedupeById(raw));
            setAgencyPage(1);
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError')
                return;
            setError(err instanceof Error ? err.message : 'Failed to load agencies');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { fetchAgencies(); }, [fetchAgencies]);
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            if (view === 'agencies')
                await fetchAgencies(search);
            else
                await fetchRequestedUsers();
        }
        finally {
            setIsRefreshing(false);
        }
    }, [view, search, fetchAgencies, fetchRequestedUsers]);
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchAgencies(search);
    };
    const toggleVisibility = (id: string) => {
        setVisibleIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const handleSubscriptionToggle = async (agency: Agency, setter: React.Dispatch<React.SetStateAction<Agency[]>>) => {
        setTogglingId(agency.agencyId);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/admin/agency/subscription/upsert`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
                body: JSON.stringify({
                    agencyId: agency.agencyId,
                    name: agency.name,
                    agentId: agency.agentId,
                    isSubscribed: !agency.isSubscribed,
                }),
            });
            if (!res.ok)
                throw new Error(`Failed: ${res.status}`);
            setter(prev => prev.map(a => a.agencyId === agency.agencyId ? { ...a, isSubscribed: !a.isSubscribed } : a));
        }
        catch (err) {
            console.error('Failed to toggle subscription:', err);
        }
        finally {
            setTogglingId(null);
        }
    };
    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };
    const agenciesSlice = agencies.slice((agencyPage - 1) * PAGE_SIZE, agencyPage * PAGE_SIZE);
    const filteredRequested = requestedSearch.trim()
        ? requestedUsers.filter(u => u.name.toLowerCase().includes(requestedSearch.toLowerCase()) ||
            u.agencyId.toLowerCase().includes(requestedSearch.toLowerCase()))
        : requestedUsers;
    const requestedSlice = filteredRequested.slice((requestedPage - 1) * PAGE_SIZE, requestedPage * PAGE_SIZE);
    return (<div className="admin-page">

      <header className="admin-topbar admin-topbar--fixed">
        <div className="admin-topbar__left">
          <div className="admin-topbar__logo-wrap">
            <img src={logo} alt="InsuranceAgency.AI" width="34" height="34"/>
            <div className="admin-topbar__logo-glow"/>
          </div>
          <div className="admin-topbar__brand-info">
            <div className="admin-topbar__brand-name">InsuranceAgency.AI</div>
            <div className="admin-topbar__brand-sub">Platform Administration</div>
          </div>
          <div className="admin-topbar__divider"/>
          <span className="admin-topbar__admin-badge">ADMIN</span>
        </div>

        <div className="admin-topbar__right">
          <div className="admin-topbar__user">
            <div className="admin-topbar__avatar">{adminInitial}</div>
            <div className="admin-topbar__user-info">
              <span className="admin-topbar__user-name">{adminName}</span>
              <span className="admin-topbar__user-role">Super Admin</span>
            </div>
          </div>
          <div className="admin-topbar__separator"/>
          <button className="admin-logout-btn" onClick={handleLogout}>
            <FiLogOut size={14}/>
            Sign out
          </button>
        </div>
      </header>

      <div className="admin-page__header">
        <div className="admin-page__title-block">
          <h1 className="admin-page__title">Admin Dashboard</h1>
          <p className="admin-page__subtitle">
            {view === 'agencies' && 'Manage agency subscriptions'}
            {view === 'requested' && (<span className="admin-page__subtitle-row">
                <FiUserCheck size={12}/>
                Users who have requested access
                {requestedUsers.length > 0 && (<span className="admin-page__subtitle-badge">{requestedUsers.length} total</span>)}
              </span>)}
            {view === 'claw' && (<span className="admin-page__subtitle-row">
                <FiZap size={12}/>
                Momentum Claw requests submitted by agencies
              </span>)}
          </p>
        </div>
      </div>

      <div className="admin-tabs">
        <div className="admin-tabs__left">
          <button className={`admin-tab${view === 'agencies' ? ' admin-tab--active' : ''}`} onClick={() => setView('agencies')}>
            Agencies
            {agencies.length > 0 && <span className="admin-tab__count">{agencies.length}</span>}
          </button>
          <button className={`admin-tab${view === 'requested' ? ' admin-tab--active' : ''}`} onClick={() => { setView('requested'); if (requestedUsers.length === 0 && !requestedLoading)
        fetchRequestedUsers(); }}>
            Requested Agencies
            {requestedUsers.length > 0 && <span className="admin-tab__count">{requestedUsers.length}</span>}
          </button>
          <button className={`admin-tab${view === 'claw' ? ' admin-tab--active' : ''}`} onClick={() => setView('claw')}>
            Momentum Claw Requests
            {clawCount !== null && <span className="admin-tab__count">{clawCount}</span>}
          </button>
        </div>
        <button className="admin-refresh-btn" onClick={handleRefresh} disabled={isRefreshing}>
          <FiRefreshCw size={13} className={isRefreshing ? 'admin-spin' : ''}/>
          <span>Refresh</span>
        </button>
      </div>

      {view === 'agencies' && (<>
          <form className="admin-search-bar" onSubmit={handleSearch}>
            <div className="admin-search-bar__input-wrap">
              <FiSearch size={15} className="admin-search-bar__icon"/>
              <input type="text" className="admin-search-bar__input" placeholder="Search agencies..." value={search} onChange={e => { setSearch(e.target.value); if (e.target.value === '')
            fetchAgencies(''); }}/>
            </div>
            <button className="admin-search-bar__btn" type="submit" disabled={loading}>Search</button>
          </form>

          <div className="admin-table-wrapper">
            {error && <div className="admin-error">{error}</div>}
            {loading ? (<div className="admin-loading">
                {[...Array(5)].map((_, i) => <div key={i} className="admin-skeleton-row"/>)}
              </div>) : (<>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Agency Name</th>
                      <th>Agency ID</th>
                      <th>Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agenciesSlice.length === 0 ? (<tr><td colSpan={4} className="admin-table__empty">No agencies found</td></tr>) : agenciesSlice.map((agency, idx) => (<tr key={agency.agencyId}>
                        <td className="admin-table__index">{(agencyPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td className="admin-table__name">{agency.name}</td>
                        <td className="admin-table__id-cell">
                          <span className={`admin-table__id-value ${visibleIds.has(agency.agencyId) ? '' : 'admin-table__id-hidden'}`}>
                            {visibleIds.has(agency.agencyId) ? agency.agencyId : '••••••••••••••••••••••••••••'}
                          </span>
                          <button className="admin-table__id-toggle" onClick={() => toggleVisibility(agency.agencyId)} title={visibleIds.has(agency.agencyId) ? 'Hide ID' : 'Show ID'}>
                            {visibleIds.has(agency.agencyId) ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                          </button>
                        </td>
                        <td>
                          <button className={`admin-toggle ${agency.isSubscribed ? 'admin-toggle--on' : 'admin-toggle--off'}`} onClick={() => handleSubscriptionToggle(agency, setAgencies)} disabled={togglingId === agency.agencyId} title={agency.isSubscribed ? 'Click to unsubscribe' : 'Click to subscribe'}>
                            <span className="admin-toggle__knob"/>
                          </button>
                        </td>
                      </tr>))}
                  </tbody>
                </table>
                <Pagination page={agencyPage} total={agencies.length} onPage={setAgencyPage}/>
              </>)}
          </div>
        </>)}

      {view === 'claw' && <ClawRequestsTab onCountChange={setClawCount}/>}

      {view === 'requested' && (<>
          <div className="admin-search-bar">
            <div className="admin-search-bar__input-wrap">
              <FiSearch size={15} className="admin-search-bar__icon"/>
              <input type="text" className="admin-search-bar__input" placeholder="Search by agency name or agency ID..." value={requestedSearch} onChange={e => { setRequestedSearch(e.target.value); setRequestedPage(1); }}/>
            </div>
          </div>
          <div className="admin-table-wrapper">
          {requestedError && <div className="admin-error">{requestedError}</div>}
          {requestedLoading ? (<div className="admin-loading">
              {[...Array(5)].map((_, i) => <div key={i} className="admin-skeleton-row"/>)}
            </div>) : (<>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Agency Name</th>
                    <th>Agency ID</th>
                    <th>Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {requestedSlice.length === 0 ? (<tr><td colSpan={4} className="admin-table__empty">No requested subscriptions found</td></tr>) : requestedSlice.map((agency, idx) => (<tr key={agency.agencyId}>
                      <td className="admin-table__index">{(requestedPage - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="admin-table__name">{agency.name}</td>
                      <td className="admin-table__id-cell">
                        <span className={`admin-table__id-value ${visibleIds.has(agency.agencyId) ? '' : 'admin-table__id-hidden'}`}>
                          {visibleIds.has(agency.agencyId) ? agency.agencyId : '••••••••••••••••••••••••••••'}
                        </span>
                        <button className="admin-table__id-toggle" onClick={() => toggleVisibility(agency.agencyId)} title={visibleIds.has(agency.agencyId) ? 'Hide ID' : 'Show ID'}>
                          {visibleIds.has(agency.agencyId) ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                        </button>
                      </td>
                      <td>
                        <button className={`admin-toggle ${agency.isSubscribed ? 'admin-toggle--on' : 'admin-toggle--off'}`} onClick={() => handleSubscriptionToggle(agency, setRequestedUsers)} disabled={togglingId === agency.agencyId} title={agency.isSubscribed ? 'Click to unsubscribe' : 'Click to subscribe'}>
                          <span className="admin-toggle__knob"/>
                        </button>
                      </td>
                    </tr>))}
                </tbody>
              </table>
              <Pagination page={requestedPage} total={filteredRequested.length} onPage={setRequestedPage}/>
            </>)}
        </div>
        </>)}
    </div>);
};
export default AdminDashboardPage;
